<template>
  <NtClientOnly>
    <Teleport to="#teleported">
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-4">
        <div
          v-if="visible"
          role="dialog"
          aria-live="polite"
          aria-labelledby="cookie-consent-heading"
          class="fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-md bg-white p-5 text-sm text-neutral-800 shadow-2xl ring-1 ring-black/5">
          <h2 id="cookie-consent-heading" class="h4 mb-2 font-heading text-base text-neutral-900">We value your privacy</h2>
          <p class="mb-4 leading-relaxed text-neutral-700">
            We use cookies to improve your experience and analyse site traffic. You can accept all cookies or decline non-essential ones.
          </p>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="btn-primary theme-rounded cursor-pointer px-4 py-2 text-sm font-medium" @click="accept">Accept</button>
            <button type="button" class="btn-secondary theme-rounded cursor-pointer px-4 py-2 text-sm font-medium" @click="decline">Decline</button>
            <NtLink
              v-if="cookiePolicyHref"
              :href="cookiePolicyHref"
              class="theme-rounded inline-flex cursor-pointer items-center px-3 py-2 text-sm font-medium text-primary-500 hover:underline">
              Cookie policy
            </NtLink>
          </div>
        </div>
      </Transition>
    </Teleport>
  </NtClientOnly>
</template>

<script>
import { SITE_EVENTS, dispatchSiteEvent } from '../../events.js'
import NtLink from './NtLink.vue'
import NtClientOnly from '@/js/components/general/NtClientOnly.vue'

const CONSENT_COOKIE = 'nt_cookie_consent'
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365

export default {
  components: { NtClientOnly, NtLink },
  data() {
    return {
      visible: false
    }
  },
  computed: {
    cookiePolicyHref() {
      const raw = this.$page.props.cookiePolicyPath
      if (!raw) {
        return null
      }
      const path = String(raw).replace(/^\/+/, '')
      return path ? `/${path}` : '/'
    }
  },
  mounted() {
    // Footer "Cookie Preferences" links re-open the dialog so the visitor can
    // change a previously saved choice.
    window.addEventListener(SITE_EVENTS.openCookieConsent, this.show)

    const choice = this.readConsent()
    if (choice === 'accepted') {
      this.activateNonEssentialScripts()
      // Re-announce the stored decision so consent-mode integrations (e.g.
      // Google Analytics) restore the granted state on every page load.
      dispatchSiteEvent(SITE_EVENTS.gdprConsented)
      return
    }
    if (choice === 'declined') {
      dispatchSiteEvent(SITE_EVENTS.gdprDenied)
      return
    }
    this.visible = true
  },
  beforeUnmount() {
    window.removeEventListener(SITE_EVENTS.openCookieConsent, this.show)
  },
  methods: {
    show() {
      this.visible = true
    },
    readConsent() {
      const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`))
      return match ? decodeURIComponent(match[1]) : null
    },
    writeConsent(value) {
      document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(value)}; path=/; max-age=${CONSENT_MAX_AGE}; SameSite=Lax`
    },
    accept() {
      this.writeConsent('accepted')
      this.visible = false
      this.activateNonEssentialScripts()
      dispatchSiteEvent(SITE_EVENTS.gdprConsented)
    },
    decline() {
      this.writeConsent('declined')
      this.visible = false
      dispatchSiteEvent(SITE_EVENTS.gdprDenied)
    },
    activateNonEssentialScripts() {
      const scripts = document.querySelectorAll('script[data-cookie-policy="nonessential"][type="text/plain"]')
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script')
        for (const attr of oldScript.attributes) {
          if (attr.name === 'type') {
            continue
          }
          newScript.setAttribute(attr.name, attr.value)
        }
        newScript.setAttribute('type', 'text/javascript')
        if (oldScript.textContent) {
          newScript.text = oldScript.textContent
        }
        oldScript.parentNode.replaceChild(newScript, oldScript)
      })
    }
  }
}
</script>
